import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    id: 'avaliacao',
    title: 'Avaliação',
    description: 'Conversa detalhada sobre seu histórico, queixas, objetivos e estilo de vida.',
    icon: 'ri-chat-heart-line',
  },
  {
    id: 'investigacao',
    title: 'Investigação',
    description: 'Análise aprofundada das possíveis causas dos seus sintomas e desequilíbrios.',
    icon: 'ri-search-eye-line',
  },
  {
    id: 'exames',
    title: 'Exames',
    description: 'Solicitação de exames laboratoriais personalizados de acordo com sua necessidade.',
    icon: 'ri-microscope-line',
  },
  {
    id: 'plano',
    title: 'Plano Individual',
    description: 'Elaboração de estratégia terapêutica exclusiva, combinando diferentes abordagens.',
    icon: 'ri-file-list-3-line',
  },
  {
    id: 'acompanhamento',
    title: 'Acompanhamento',
    description: 'Monitoramento contínuo dos resultados com ajustes sempre que necessário.',
    icon: 'ri-heart-pulse-line',
  },
];

function StepItem({ step, index, inView }: { step: typeof steps[0]; index: number; inView: boolean }) {
  return (
    <div
      className="flex flex-col items-center text-center relative"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.7s ease-out ${index * 150}ms`,
      }}
    >
      <div
        className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-full flex items-center justify-center mb-5 relative z-10 transition-all duration-500"
        style={{
          backgroundColor: index === 0 ? 'rgba(200, 169, 106, 0.15)' : 'rgba(248, 247, 245, 0.03)',
          border: index === 0
            ? '1px solid rgba(200, 169, 106, 0.3)'
            : '1px solid rgba(248, 247, 245, 0.06)',
        }}
      >
        <i
          className={`${step.icon} text-xl md:text-2xl`}
          style={{ color: index === 0 ? '#C8A96A' : 'rgba(248, 247, 245, 0.4)' }}
        />
      </div>

      {index < steps.length - 1 && (
        <div className="hidden md:block absolute top-[36px] left-[calc(50%+44px)] w-[calc(100%-88px)] h-[1px]">
          <div
            className="h-full transition-all duration-1000 delay-700"
            style={{
              background: `linear-gradient(90deg, rgba(200, 169, 106, 0.3) 0%, rgba(248, 247, 245, 0.05) 100%)`,
              width: inView ? '100%' : '0%',
            }}
          />
        </div>
      )}

      <h4
        className="text-lg md:text-xl mb-2"
        style={{
          fontFamily: 'var(--font-heading)',
          color: '#F8F7F5',
          fontWeight: 600,
        }}
      >
        {step.title}
      </h4>
      <p
        className="text-xs md:text-sm leading-relaxed max-w-[200px]"
        style={{
          fontFamily: 'var(--font-body)',
          color: 'rgba(248, 247, 245, 0.4)',
          fontWeight: 300,
          lineHeight: 1.7,
        }}
      >
        {step.description}
      </p>
    </div>
  );
}

export default function ComoFunciona() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#0E1412' }}>
      <div
        className="absolute top-0 left-0 right-0 h-[1px]"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(200, 169, 106, 0.15) 50%, transparent 100%)' }}
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16 md:mb-20">
          <p
            className="text-sm tracking-[0.15em] mb-4"
            style={{
              fontFamily: 'var(--font-body)',
              color: '#C8A96A',
              fontWeight: 500,
            }}
          >
            COMO FUNCIONA
          </p>
          <h2
            className="text-3xl md:text-5xl"
            style={{
              fontFamily: 'var(--font-heading)',
              color: '#F8F7F5',
              fontWeight: 500,
              lineHeight: 1.2,
            }}
          >
            O caminho para sua transformação
          </h2>
        </div>

        <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-0 md:justify-between relative max-w-[900px] mx-auto">
          {steps.map((step, index) => (
            <StepItem key={step.id} step={step} index={index} inView={inView} />
          ))}
        </div>
      </div>
    </section>
  );
}